import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { RadioGroup } from '../../../../components/inputs/RadioGroup';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { formatBytes } from '../../../../lib/formatBytes';
import { focusValidationError } from '../../../../lib/validationFocus';
import { evidenceFileTypesApi } from '../evidenceApi';
import type { EvidenceFileTypeItem } from '../evidenceTypes';

const MB = 1024 * 1024;

/**
 * Switches one evidence file type on or off, and sets how large it may be — product call,
 * 07 Oct 2026, "make it configurable per type". The LIST is the code's: every type here is one
 * the server can verify by its magic bytes, so nobody adds or removes a row.
 */
export function EvidenceFileTypeModal({
  fileType,
  onClose,
  onSaved,
}: {
  fileType: EvidenceFileTypeItem;
  onClose: () => void;
  onSaved: () => void;
}) {
  const ceilingMb = Math.floor(fileType.ceilingBytes / MB);

  const [enabled, setEnabled] = useState(fileType.isEnabled ? 'yes' : 'no');
  const [limitMb, setLimitMb] = useState(String(Math.round(fileType.maxSizeBytes / MB)));
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const parsed = Number(limitMb);
  const validLimit = Number.isInteger(parsed) && parsed >= 1 && parsed <= ceilingMb;
  const canSave = validLimit && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      await evidenceFileTypesApi.update(fileType.id, { isEnabled: enabled === 'yes', maxSizeBytes: parsed * MB });
      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the file type.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'evidence-file-type', fieldIds: { maxSizeBytes: 'evidence-file-type-limit' } });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Edit ${fileType.name}`}
      size="md"
      id="evidence-file-type-modal"
      autogenerate={{
        onGenerate: () => {
          setEnabled('yes');
          setLimitMb(String(Math.max(1, Math.floor(ceilingMb / 2))));
        },
        disabled: saving,
      }}
      confirm={{ id: 'evidence-file-type-confirm', checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id="evidence-file-type-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="evidence-file-type-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <RadioGroup
          id="evidence-file-type-is-enabled"
          label="Accepted as evidence"
          required
          value={enabled}
          onChange={setEnabled}
          disabled={saving}
          options={[
            { value: 'yes', label: 'Accepted', description: `${fileType.extensions.join(', ')} files can be filed as evidence and as custody documents.` },
            { value: 'no', label: 'Not accepted', description: 'Refused on every evidence form. Files already filed are kept.' },
          ]}
        />

        <TextField
          id="evidence-file-type-limit"
          label="Size limit (MB)"
          required
          inputMode="numeric"
          description={`Whole megabytes, from 1 to ${ceilingMb}. ${ceilingMb} MB (${formatBytes(fileType.ceilingBytes)}) is the system's own ceiling — the file store holds a file in memory while it encrypts it.`}
          value={limitMb}
          onChange={(e) => setLimitMb(e.target.value.replace(/[^0-9]/g, ''))}
          disabled={saving}
          error={!validLimit && limitMb !== '' ? `Enter a whole number from 1 to ${ceilingMb}.` : error?.fieldError('maxSizeBytes')}
        />
      </div>
    </Modal>
  );
}
